import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { config, isDemo } from '../config';
import type { Role } from '../domain/types';
import { useI18n } from '../i18n/I18nProvider';
import type { StringKey } from '../i18n/strings';

export function roleKey(r: Role | undefined): StringKey {
  return r === 'admin' ? 'roleAdmin' : r === 'planner' ? 'rolePlanner' : r === 'collector' ? 'roleCollector' : 'roleViewer';
}

export function Nav() {
  const { t, lang, setLang } = useI18n();
  const { profile, status, signOut, isCollector } = useAuth();
  const links: [string, StringKey][] = isCollector
    ? [['/collect', 'navCollect']]
    : [
        ['/', 'navHome'],
        ['/manifest', 'navManifest'],
        ['/programs', 'navPrograms'],
        ['/collect', 'navCollect'],
        ['/stock', 'navStock'],
        ['/history', 'navHistory'],
      ];
  if (status === 'ready' && profile?.role === 'admin') links.push(['/team', 'navTeam']);
  const showLinks = status === 'ready';
  // The bar stays on top. On a phone, where it takes two rows, it slides away while scrolling
  // down and comes back as soon as the page is scrolled up.
  const [tucked, setTucked] = useState(false);
  useEffect(() => {
    const small = window.matchMedia('(max-width: 759px)');
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) < 6) return;
      setTucked(small.matches && y > lastY && y > 120);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // what the bar covers at the top, for the bars that stick under it (--nav-h)
  const navEl = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = navEl.current;
    if (!el) return;
    const put = () => document.documentElement.style.setProperty('--nav-h', (tucked ? 0 : el.offsetHeight) + 'px');
    put();
    const ro = new ResizeObserver(put);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tucked]);
  return (
    <nav ref={navEl} className={'uco-nav' + (tucked ? ' tucked' : '')}>
      <Link className="uco-brand" to="/">
        <span className="uco-mark">◆</span>
        {config.siteName}
      </Link>
      {showLinks && (
        <div className="uco-links">
          {links.map(([to, key]) => (
            <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => (isActive ? 'on' : '')}>
              {t(key)}
            </NavLink>
          ))}
        </div>
      )}
      <span className="uco-sp" />
      {isDemo && (
        <span className="uco-demo" title={t('demoTip')}>
          {t('demoBadge')}
        </span>
      )}
      <div className="uco-lang" role="group" aria-label="Language">
        <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>
          EN
        </button>
        <button className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>
          FR
        </button>
      </div>
      {profile && (
        <span className="uco-who">
          {profile.full_name || profile.email || ''}
          {profile.role && status === 'ready' && <span className="uco-role">{t(roleKey(profile.role))}</span>}
        </span>
      )}
      {!isDemo && profile && (
        <button className="uco-out" onClick={() => void signOut()}>
          {t('signOut')}
        </button>
      )}
    </nav>
  );
}
